import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { useEmpresa } from "../contexts/EmpresaContext";
import { UNIDADES as UNIDADES_TODAS } from "../constants/unidades";
import { nombreDeRol } from "../constants/roles";
import { NAV_ITEMS, NAV_GROUPS, MODULOS_POR_ROL } from "../constants/modulos";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { colors, font } from "../styles/theme";

export default function Layout({ currentPage, onNavigate, onCambiarEmpresa, children }) {
  const { userProfile, logout, rol } = useAuth();
  const { empresa } = useEmpresa();
  const [collapsed, setCollapsed] = useState(false);
  const [badges, setBadges] = useState({ gastos: 0 });
  const [gruposAbiertos, setGruposAbiertos] = useState(() => {
    try {
      const guardado = JSON.parse(localStorage.getItem("sidebar_grupos_abiertos"));
      if (guardado) return guardado;
    } catch (err) {}
    return Object.fromEntries(NAV_GROUPS.map(g => [g.id, true]));
  });

  function toggleGrupo(id) {
    setGruposAbiertos(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem("sidebar_grupos_abiertos", JSON.stringify(next));
      return next;
    });
  }

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  async function fetchBadges() {
    try {
      const hoy = new Date().toISOString().split("T")[0];
      const en7dias = new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0];
      const snap = await getDocs(query(
        collection(db, "facturas"),
        where("estado", "==", "pendiente"),
        where("fechaVencimiento", "<=", en7dias),
        limit(99)
      ));
      setBadges({ gastos: snap.size });
    } catch (err) {}
  }

  const visibleItems = NAV_ITEMS.filter(item => {
    if (!item.roles.includes(rol)) return false;
    if (item.ocultoEnEmpresas?.includes(empresa)) return false;
    // Un usuario con permisos personalizados (ver Usuarios) manda sobre el
    // set por default de su rol.
    if (userProfile?.modulosPermitidos) return userProfile.modulosPermitidos.includes(item.id);
    // Roles operativos sin personalización ven su set reducido de módulos
    if (MODULOS_POR_ROL[rol]) return MODULOS_POR_ROL[rol].includes(item.id);
    return true;
  });
  const { unidadAsignada, unidadesAsignadas } = useAuth();
  const unidadLabel = UNIDADES_TODAS.find(u => u.id === unidadAsignada)?.nombreCorto
    || (unidadesAsignadas.length ? `${unidadesAsignadas.length} unidades` : "");
  const rolLabel = `${nombreDeRol(rol)}${unidadLabel ? " · " + unidadLabel : ""}`;
  const currentItem = NAV_ITEMS.find(i => i.id === currentPage);

  const itemSuelto = visibleItems.filter(i => !i.group);
  const gruposConItems = NAV_GROUPS
    .map(g => ({ ...g, items: visibleItems.filter(i => i.group === g.id) }))
    .filter(g => g.items.length > 0);

  function renderNavBtn(item) {
    return (
      <button key={item.id} title={collapsed ? item.label : ""}
        style={{ ...s.navBtn, background: currentPage === item.id ? "#2a2218" : "transparent", borderLeft: currentPage === item.id ? "3px solid #C8A96E" : "3px solid transparent", color: currentPage === item.id ? "#C8A96E" : "#888", position: "relative" }}
        onClick={() => onNavigate(item.id)}>
        <span style={s.navIcon}>{item.icon}</span>
        {!collapsed && <span style={s.navLabel}>{item.label}</span>}
        {item.id === "gastos" && badges.gastos > 0 && (
          <span style={{
            position: "absolute",
            top: "8px",
            right: collapsed ? "6px" : "12px",
            background: "#e05555",
            color: "#fff",
            borderRadius: "10px",
            fontSize: "10px",
            fontWeight: "700",
            minWidth: "18px",
            height: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
            fontFamily: "sans-serif"
          }}>
            {badges.gastos > 99 ? "99+" : badges.gastos}
          </span>
        )}
      </button>
    );
  }

  return (
    <div style={s.root}>
      <aside style={{ ...s.sidebar, width: collapsed ? "64px" : "220px" }}>
        <div style={s.sidebarTop}>
          <div style={s.logoWrap}>
            {empresa === "urban" ? (
              <img src="/urban-logo.png" alt="Urban" style={s.logoImg} />
            ) : (
              <span style={s.logoEmoji}>🍣</span>
            )}
            {!collapsed && <span style={s.logoText}>{empresa === "urban" ? "URBAN" : "SUSHIITO"}</span>}
          </div>
          <button style={s.collapseBtn} onClick={() => setCollapsed(c => !c)}>
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {(rol === "dueno" || rol === "admin") && onCambiarEmpresa && (
          <button
            onClick={onCambiarEmpresa}
            title="Cambiar de empresa"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: "8px",
              margin: "10px 12px",
              padding: "8px 10px",
              borderRadius: "7px",
              border: "1px solid #2a2218",
              cursor: "pointer",
              fontSize: "12px",
              fontFamily: "sans-serif",
              fontWeight: "600",
              background: "#1a1611",
              color: "#C8A96E"
            }}
          >
            <span>🔁</span>
            {!collapsed && <span>Cambiar empresa</span>}
          </button>
        )}

        <nav style={s.nav}>
          {itemSuelto.map(renderNavBtn)}
          {collapsed
            ? gruposConItems.flatMap(g => g.items.map(renderNavBtn))
            : gruposConItems.map(g => (
              <div key={g.id} style={s.groupWrap}>
                <button style={s.groupHeader} onClick={() => toggleGrupo(g.id)}>
                  <span style={s.groupIcon}>{g.icon}</span>
                  <span style={s.groupLabel}>{g.label}</span>
                  <span style={s.groupChevron}>{gruposAbiertos[g.id] ? "▾" : "▸"}</span>
                </button>
                {gruposAbiertos[g.id] && g.items.map(renderNavBtn)}
              </div>
            ))
          }
        </nav>

        <div style={s.sidebarBottom}>
          {!collapsed && (
            <div style={s.userInfo}>
              <p style={s.userName}>{userProfile?.nombre || "Usuario"}</p>
              <p style={s.userRol}>{rolLabel}</p>
            </div>
          )}
          <button style={s.logoutBtn} onClick={logout}>
            <span>🚪</span>
            {!collapsed && <span style={{ marginLeft: "8px" }}>Salir</span>}
          </button>
        </div>
      </aside>

      <main style={s.main}>
        <div style={{
          ...s.topbar,
          background: empresa === "urban" ? colors.urban : colors.sushiito,
          borderBottom: empresa === "urban" ? "2px solid #6b2a1f" : "2px solid #123f66",
          boxShadow: empresa === "urban" ? "0 2px 8px rgba(140,53,39,0.3)" : "0 2px 8px rgba(29,90,150,0.3)",
        }}>
          <h2 style={s.pageTitle}>{currentItem?.icon} {currentItem?.label}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <span style={{ ...s.dateLabel, color: empresa === "urban" ? "#f0c8be" : "#b8d4f0" }}>
              {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            <img
              src="/logo-goam.png"
              alt="GOAM"
              title="Ir al Dashboard"
              onClick={() => onNavigate("dashboard")}
              style={{ height: "34px", width: "34px", objectFit: "contain", cursor: "pointer" }}
            />
          </div>
        </div>
        <div style={s.content}>{children}</div>
      </main>
    </div>
  );
}

const s = {
  root: { display: "flex", minHeight: "100vh", background: colors.paper, fontFamily: font.sans },
  sidebar: { background: colors.charcoal, borderRight: "1px solid #2a2418", display: "flex", flexDirection: "column", transition: "width 0.2s", flexShrink: 0 },
  sidebarTop: { padding: "20px 16px", borderBottom: "1px solid #2a2418", display: "flex", alignItems: "center", justifyContent: "space-between" },
  logoWrap: { display: "flex", alignItems: "center", gap: "10px" },
  logoEmoji: { fontSize: "22px" },
  logoImg: { width: "26px", height: "26px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  logoText: { color: colors.gold, fontFamily: font.serif, letterSpacing: "3px", fontSize: "13px", fontWeight: "normal" },
  collapseBtn: { background: "transparent", border: "none", color: "#444", cursor: "pointer", fontSize: "18px", padding: "0 4px" },
  nav: { flex: 1, padding: "10px 0", overflowY: "auto" },
  groupWrap: { marginTop: "6px" },
  groupHeader: { display: "flex", alignItems: "center", width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: "9px 16px", gap: "8px", textAlign: "left" },
  groupIcon: { fontSize: "12px", flexShrink: 0 },
  groupLabel: { flex: 1, fontSize: "11px", fontWeight: "700", letterSpacing: "0.6px", textTransform: "uppercase", color: "#5a5548" },
  groupChevron: { fontSize: "10px", color: "#5a5548" },
  navBtn: { display: "flex", alignItems: "center", width: "100%", border: "none", cursor: "pointer", padding: "13px 16px", gap: "12px", transition: "all 0.15s", textAlign: "left" },
  navIcon: { fontSize: "16px", flexShrink: 0, width: "20px", textAlign: "center" },
  navLabel: { fontSize: "15px", whiteSpace: "nowrap", fontWeight: "500" },
  sidebarBottom: { padding: "16px", borderTop: "1px solid #2a2418" },
  userInfo: { marginBottom: "12px" },
  userName: { color: "#bbb", fontSize: "13px", margin: "0 0 2px" },
  userRol: { color: colors.gold, fontSize: "11px", margin: 0, letterSpacing: "1px", textTransform: "uppercase" },
  logoutBtn: { display: "flex", alignItems: "center", width: "100%", background: "transparent", border: "1px solid #2a2a2a", borderRadius: "6px", padding: "9px 12px", color: "#666", cursor: "pointer", fontSize: "13px" },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "visible" },
  topbar: { background: colors.sushiito, borderBottom: "2px solid #0d3d7a", padding: "0 28px", height: "62px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 8px rgba(26,86,160,0.3)" },
  pageTitle: { color: "#ffffff", fontWeight: "600", fontSize: "18px", margin: 0 },
  dateLabel: { color: "#b8d4f0", fontSize: "13px", textTransform: "capitalize", fontWeight: "500" },
  content: { flex: 1, overflow: "auto", padding: "24px", background: colors.paper }
};